import { Modal } from '@ui';
import type { FC } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { reaction, when } from 'mobx';
import GuestDeliveryPickupModal from '@components/AddressSelection/GuestDeliveryPickupModal';
import isEmpty from 'lodash/isEmpty';
import type { storeAddressT } from '@store/UserStore.type';
import { createAddressString } from '@utils/address';
import useAddressForm from '@customHooks/useAddressForm';
import useAddressMap from '@customHooks/useAddressMap';
import useAddressSave from '@customHooks/useAddressSave';
import useWindowSize from '@customHooks/useWindowSize';
import { useStores } from '@root/src/stores';

interface ModalMapfuncProps {
  changeModalState: (state: boolean) => void;
  pickups: any[];
  triggerChangePickupStore: (store: any) => void;
  setShowModal: (state: boolean) => void;
  activeStore: any;
  isHideDelivery?: boolean;
  isShowClusterer?: boolean;
  isShouldAutoSwitchPickupStore?: boolean;
  availableQuantity?: Array<{
    store_id: number;
    amount: number;
  }>;
  isShowAvailableQuantity?: boolean;
  initialState?: storeAddressT;
}

const ModalMapfunc: FC<ModalMapfuncProps> = observer(
  ({
    changeModalState,
    pickups,
    triggerChangePickupStore,
    setShowModal,
    activeStore,
    isHideDelivery,
    isShouldAutoSwitchPickupStore,
    availableQuantity,
    isShowAvailableQuantity,
    initialState
  }) => {
    const { cartStore, userStore } = useStores();
    const { isMobile } = useWindowSize();
    const isFirstOrderAddressRef = useRef(true);

    const {
      fields,
      setFields,
      setField,
      editAddress: setFormAddress
    } = useAddressForm({ isAddAddress: true });

    const {
      address,
      coords,
      isShowMapMarkPlace,
      balloonState,
      selectedAddress,
      isAddAddress: editAddress
    } = fields;

    const editAddressRef = useRef(editAddress);
    editAddressRef.current = editAddress;

    const hasPickedPointRef = useRef(false);
    hasPickedPointRef.current = !!fields.address || !!fields.dadataId;

    const {
      onSelectDaData,
      onMapClick,
      createTemplateLayoutFactory: initMap,
      isDefaultMark: isDefaultMarkByCoords,
      iconContentTemplate,
      mapRefs
    } = useAddressMap({
      setFields,
      onAfterAddressAccepted: ({ dadataId, source }) => {
        if (isFirstOrderAddressRef.current && dadataId) {
          cartStore.updateCartStage({
            dadataId: Number(dadataId),
            addressConfirmedByUser: source === 'balloon'
          });
        }
      },
      isEditMode: () => editAddressRef.current
    });

    const createTemplateLayoutFactory = useCallback(
      (ymaps: any) => {
        initMap(ymaps);
        if (cartStore.isCartAddressFromOtherRegion) return;
        const { geoLat, geoLon } = cartStore?.cart?.dadata || {};
        const lat = Number(geoLat);
        const lon = Number(geoLon);
        if (Number.isFinite(lat) && Number.isFinite(lon)) {
          setFields({
            coords: [lat, lon]
          });
        }
      },
      [initMap, cartStore, setFields]
    );

    const { selectAddress } = useAddressSave({
      userStore,
      fields,
      setFields,
      onAfterSelect: (addr: any) => {
        cartStore.updateCartStage({ peopleAddressId: addr.id });
      }
    });

    const isDefaultMark = useCallback(
      () => isDefaultMarkByCoords(coords),
      [isDefaultMarkByCoords, coords]
    );

    const editAddressFn = useCallback(
      (addr: any, e?: any) => {
        if (e) e.stopPropagation();
        setFormAddress(addr);
        setField('isAddAddress', true);
        cartStore.updateCartStage({ peopleAddressId: addr.id });
      },
      [setFormAddress, setField, cartStore]
    );

    useEffect(() => {
      if (initialState) {
        editAddressFn(initialState);
      }

      const disposer1 = when(
        () => {
          const { isLoadCart } = cartStore;
          const { isLoad, user } = userStore;
          return isLoadCart && isLoad && isEmpty(user);
        },
        () => {
          const { cart } = cartStore;
          setFields({
            address: createAddressString({ dadata: cart.dadata } as any)
          });
        }
      );

      const disposer2 = when(
        () => {
          const { isLoadCart } = cartStore;
          const { isLoad, user } = userStore;
          return isLoad && isLoadCart && !isEmpty(user);
        },
        () => {
          const { presortedAddresses } = userStore;
          const { cart } = cartStore;
          const selAddr = cart.peopleAddress ? cart.peopleAddress.id : '';
          if (
            selAddr &&
            !isHideDelivery &&
            !cartStore.isCartAddressFromOtherRegion
          ) {
            const currentAddress = presortedAddresses?.find(
              i => i.id === selAddr
            );
            if (currentAddress) selectAddress(currentAddress);
          }
        }
      );

      const disposerRegionCenter = reaction(
        () => cartStore.regionData?.fields?.geo_center?.join(','),
        () => {
          const center = cartStore.regionData?.fields?.geo_center;
          if (center?.length && !hasPickedPointRef.current) {
            setFields({ coords: center });
          }
        }
      );

      return () => {
        disposer1();
        disposer2();
        disposerRegionCenter();
        mapRefs.current = {};
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const preSortedAddresses = userStore.moscowAddresses;

    const mapState = {
      mapRef: (ref: any) => {
        mapRefs.current.mapRef = ref;
      },
      onMapClick,
      onLoadMap: createTemplateLayoutFactory,
      coordsN: coords,
      editAddress,
      iconContentTemplate,
      address,
      isDefaultMark,
      userStore,
      preSortedAddresses,
      selectedAddress,
      selectAddress,
      onSelectDaData,
      balloonState,
      isShowMapMarkPlace
    };

    return (
      <Modal
        className='modal-with-route modal-pickup'
        withoutCross={isMobile}
        crossOutside={!isMobile}
        onClose={() => {
          changeModalState(false);
        }}
      >
        <GuestDeliveryPickupModal
          pickups={pickups}
          triggerChangePickupStore={triggerChangePickupStore}
          setShowModal={setShowModal}
          activeStore={activeStore}
          mapState={mapState}
          isHideDelivery={isHideDelivery}
          availableQuantity={availableQuantity}
          isShouldAutoSwitchPickupStore={
            !isHideDelivery ? true : isShouldAutoSwitchPickupStore
          }
          isShowAvailableQuantity={isShowAvailableQuantity}
        />
      </Modal>
    );
  }
);

export default ModalMapfunc;
